import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export type ErrorType =
  | 'INSUFFICIENT_BALANCE'
  | 'PAYMENT_REQUIRED'
  | 'AUTH_REQUIRED'
  | 'SESSION_EXPIRED'
  | 'WORKSPACE_REQUIRED'
  | 'NO_WORKSPACE_ACCESS'
  | 'VALIDATION_FAILED'
  | 'BANNED_KEYWORDS'
  | 'NETWORK_ERROR'
  | 'RATE_LIMIT'
  | 'SAVE_FAILED'
  | 'LOAD_FAILED'
  | 'DUPLICATE_KEY'
  | 'UNKNOWN';

interface ErrorAction {
  label: string;
  path?: string;
  onClick?: (() => void) | 'retry';
}

interface UserFriendlyError {
  title: string;
  message: string;
  action?: ErrorAction | null;
}

export const USER_FRIENDLY_ERRORS: Record<ErrorType, UserFriendlyError> = {
  // Payment/Wallet Errors
  INSUFFICIENT_BALANCE: {
    title: "Insufficient Funds",
    message: "You don't have enough balance to complete this action. Please add funds to continue.",
    action: { label: "Add Funds", path: "/agency/wallet" }
  },
  PAYMENT_REQUIRED: {
    title: "Insufficient Funds", 
    message: "Please add funds to your wallet to continue.",
    action: { label: "Add Funds", path: "/agency/wallet" }
  },
  
  // Authentication Errors
  AUTH_REQUIRED: {
    title: "Please Log In",
    message: "You need to log in to access this feature.",
    action: { label: "Refresh Page", onClick: () => window.location.reload() }
  },
  SESSION_EXPIRED: {
    title: "Session Expired",
    message: "Your session has expired. Please refresh the page.",
    action: { label: "Refresh Now", onClick: () => window.location.reload() }
  },
  
  // Workspace Errors  
  WORKSPACE_REQUIRED: {
    title: "Select a Workspace",
    message: "Please select a client workspace from the sidebar to continue.",
    action: null
  },
  NO_WORKSPACE_ACCESS: {
    title: "Access Denied",
    message: "You don't have permission to access this workspace.",
    action: null
  },
  
  // Validation Errors
  VALIDATION_FAILED: {
    title: "Invalid Input",
    message: "Please check your input and try again.",
    action: null
  },
  BANNED_KEYWORDS: {
    title: "Content Not Allowed",
    message: "Your content contains words that are not allowed. Please remove them and try again.",
    action: null
  },
  
  // Network Errors
  NETWORK_ERROR: {
    title: "Connection Issue",
    message: "Unable to connect. Please check your internet and try again.",
    action: { label: "Try Again", onClick: 'retry' }
  },
  RATE_LIMIT: {
    title: "Too Many Requests",
    message: "You're making requests too quickly. Please wait a moment.",
    action: null
  },
  
  // Database Errors
  SAVE_FAILED: {
    title: "Save Failed",
    message: "We couldn't save your changes. Please try again.",
    action: { label: "Try Again", onClick: 'retry' }
  },
  LOAD_FAILED: {
    title: "Loading Failed",
    message: "We couldn't load your data. Please refresh the page.",
    action: { label: "Refresh", onClick: () => window.location.reload() }
  },
  DUPLICATE_KEY: {
    title: "Already Exists",
    message: "This item already exists. Please try a different name.",
    action: null
  },
  
  // Default
  UNKNOWN: {
    title: "Something Went Wrong",
    message: "An unexpected error occurred. Please try again or contact support.",
    action: null
  }
};

/**
 * Detect error type from error object
 */
export function getErrorType(error: any): ErrorType {
  const message = error?.message?.toLowerCase() || '';
  const errorCode = error?.error_code || error?.code || error?.error;

  // Check error codes first
  if (errorCode) {
    switch (errorCode) {
      case 'INSUFFICIENT_BALANCE':
      case 'insufficient_balance':
        return 'INSUFFICIENT_BALANCE';
      case 'AUTH_REQUIRED':
      case 'auth_required':
        return 'AUTH_REQUIRED';
      case 'WORKSPACE_REQUIRED':
      case 'workspace_required':
        return 'WORKSPACE_REQUIRED';
      case 'VALIDATION_FAILED':
      case 'validation_failed':
        return 'VALIDATION_FAILED';
      case 'BANNED_KEYWORDS':
      case 'banned_keywords':
        return 'BANNED_KEYWORDS';
      case 'FORBIDDEN':
      case 'forbidden':
        return 'NO_WORKSPACE_ACCESS';
      case 'RATE_LIMIT_EXCEEDED':
      case 'rate_limit_exceeded':
        return 'RATE_LIMIT';
      case '23505': // PostgreSQL duplicate key
        return 'DUPLICATE_KEY';
    }
  }

  // Check status codes
  if (error?.status === 402 || message.includes('402') || message.includes('payment required')) {
    return 'INSUFFICIENT_BALANCE';
  }
  if (error?.status === 401 || message.includes('unauthorized') || message.includes('jwt') || message.includes('session')) {
    return 'SESSION_EXPIRED';
  }
  if (error?.status === 429 || message.includes('rate limit')) {
    return 'RATE_LIMIT';
  }

  // Check message content
  if (message.includes('insufficient') && (message.includes('balance') || message.includes('funds'))) {
    return 'INSUFFICIENT_BALANCE';
  }
  if (message.includes('workspace') && message.includes('required')) {
    return 'WORKSPACE_REQUIRED';
  }
  if (message.includes('banned') && message.includes('keyword')) {
    return 'BANNED_KEYWORDS';
  }
  if (message.includes('network') || message.includes('fetch failed') || message.includes('connection')) {
    return 'NETWORK_ERROR';
  }
  if (message.includes('validation') || message.includes('invalid')) {
    return 'VALIDATION_FAILED';
  }

  return 'UNKNOWN';
}

/**
 * Extract banned keywords from error message or response
 */
function extractBannedKeywords(error: any): string[] {
  // Check if error has banned_keywords array in response
  if (error?.banned_keywords && Array.isArray(error.banned_keywords)) {
    return error.banned_keywords;
  }
  
  // Check if error context has banned_keywords
  if (error?.context?.banned_keywords && Array.isArray(error.context.banned_keywords)) {
    return error.context.banned_keywords;
  }
  
  // Fallback: extract from message
  const message = error?.message || error?.details || '';
  const keywords: string[] = [];
  
  // Match patterns like: Banned keyword: "SaaS" or Banned keyword: 'leverage'
  const regex = /Banned keyword: ["']([^"']+)["']/gi;
  let match;
  
  while ((match = regex.exec(message)) !== null) {
    keywords.push(match[1]);
  }
  
  return keywords;
}

/**
 * Show user-friendly error toast
 */
export function showUserFriendlyError(
  errorOrType: Error | ErrorType | any,
  customMessage?: string,
  onRetry?: () => void
) {
  const errorType = typeof errorOrType === 'string' 
    ? errorOrType as ErrorType
    : getErrorType(errorOrType);

  const errorInfo = USER_FRIENDLY_ERRORS[errorType] || USER_FRIENDLY_ERRORS.UNKNOWN;
  
  // Special handling for banned keywords - extract and show specific keywords
  let message = customMessage || errorInfo.message;
  if (errorType === 'BANNED_KEYWORDS' && !customMessage) {
    const bannedKeywords = extractBannedKeywords(errorOrType);
    if (bannedKeywords.length > 0) {
      message = `Your content contains banned keywords: ${bannedKeywords.map(k => `"${k}"`).join(', ')}. Please remove them and try again.`;
    }
  }

  // Show toast with action if available
  if (errorInfo.action) {
    toast.error(errorInfo.title, {
      description: message,
      duration: 6000,
      action: errorInfo.action.path 
        ? {
            label: errorInfo.action.label,
            onClick: () => window.location.href = errorInfo.action!.path!
          }
        : errorInfo.action.onClick === 'retry' && onRetry
        ? {
            label: errorInfo.action.label,
            onClick: onRetry
          }
        : errorInfo.action.onClick && typeof errorInfo.action.onClick === 'function'
        ? {
            label: errorInfo.action.label,
            onClick: errorInfo.action.onClick
          }
        : undefined
    });
  } else {
    toast.error(errorInfo.title, {
      description: message,
      duration: 5000
    });
  }

  // Log for debugging
  console.error('[USER_FRIENDLY_ERROR]', {
    errorType,
    originalError: errorOrType,
    timestamp: new Date().toISOString()
  });
}
